import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { Line } from 'react-chartjs-2';
import { 
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
    Title, Tooltip, Legend, Filler 
} from 'chart.js';
import { 
    FaMagic, FaServer, FaDollarSign, FaExclamationTriangle, 
    FaLightbulb, FaRocket, FaShieldAlt, FaMicrochip
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './SystemHubNexus.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default function SystemHubNexus() {
    const [baseData, setBaseData] = useState(null);
    const [expansion, setExpansion] = useState(0); 
    const [growth, setGrowth] = useState(0);       
    const [ticketMedio, setTicketMedio] = useState(150);

    useEffect(() => {
        api.get('/api/system-hub/nexus/simulation-data')
            .then(res => setBaseData(res.data))
            .catch(err => {
                console.error(err);
                toast.error("Falha ao sincronizar com o Core. Verifique o console.");
            });
    }, []);

    // Memo para evitar o erro "toFixed" de propriedades undefined
    const metrics = useMemo(() => {
        if (!baseData) return { novaCarga: 0, novoTotalLicencas: 0, risoSaturacao: 0, lucroAdicional: 0, receitaProjetada: 0 };
        
        const cargaBase = Number(baseData.usuariosOnline) || 0;
        const licBase = Number(baseData.totalLicencasCap) || 0;
        const capServidor = Number(baseData.serverCapacity) || 5000;

        const novaCarga = Math.round(cargaBase * (1 + growth / 100));
        const novoTotalLicencas = Math.round(licBase * (1 + expansion / 100));
        const risoSaturacao = (novaCarga / capServidor) * 100;
        
        const receitaAtual = licBase * ticketMedio;
        const receitaProjetada = novoTotalLicencas * ticketMedio;

        return { 
            novaCarga, 
            novoTotalLicencas, 
            risoSaturacao: isNaN(risoSaturacao) ? 0 : risoSaturacao, 
            lucroAdicional: receitaProjetada - receitaAtual, 
            receitaProjetada 
        };
    }, [baseData, expansion, growth, ticketMedio]);

    if (!baseData) return (
        <div className="nexus-loading-screen">
            <div className="nexus-loader"></div>
            <span>INICIALIZANDO PROTOCOLOS NEXUS...</span>
        </div>
    );

    return (
        <div className="nexus-container">
            <header className="nexus-header">
                <div className="header-info">
                    <h1><FaMicrochip className="glow-icon" /> Nexus Twin <span>Command Center</span></h1>
                    <p>Simulação Preditiva de Infraestrutura e Receita da <strong>BLB System</strong></p>
                </div>
                <div className={`system-status ${metrics.risoSaturacao > 85 ? 'status-critical' : 'status-ok'}`}>
                    <FaShieldAlt /> {metrics.risoSaturacao > 85 ? 'INSTABILIDADE PREVISTA' : 'SISTEMA INTEGRAL'}
                </div>
            </header>

            <div className="nexus-main-layout">
                {/* COLUNA ESQUERDA: CONTROLES */}
                <aside className="nexus-controls">
                    <section className="nexus-card">
                        <h3><FaMagic /> Variáveis de Escala</h3>
                        <div className="slider-group">
                            <label>Expansão de Mercado (Licenças) <span>+{expansion}%</span></label>
                            <input type="range" min="0" max="300" value={expansion} onChange={(e) => setExpansion(Number(e.target.value))} />
                        </div>
                        <div className="slider-group">
                            <label>Carga de Acessos (Stress) <span>+{growth}%</span></label>
                            <input type="range" min="0" max="1000" value={growth} onChange={(e) => setGrowth(Number(e.target.value))} />
                        </div>
                    </section>

                    <section className="nexus-card finance-card">
                        <h3><FaDollarSign /> Projeção ROI</h3>
                        <div className="roi-value">
                            <small>Faturamento Adicional Estimado</small>
                            <h2>R$ {(metrics.lucroAdicional || 0).toLocaleString()}</h2>
                        </div>
                        <div className="input-group">
                            <label>Preço Unitário da Licença (R$)</label>
                            <input type="number" value={ticketMedio} onChange={(e) => setTicketMedio(Number(e.target.value))} />
                        </div>
                    </section>
                </aside>

                {/* COLUNA DIREITA: MONITORAMENTO */}
                <main className="nexus-monitor">
                    <div className="monitor-top">
                        <section className={`nexus-card gauge-card ${metrics.risoSaturacao > 80 ? 'warn' : ''}`}>
                            <h3>Saturação do Cluster</h3>
                            <div className="gauge-display">
                                <span className="percentage">{(metrics.risoSaturacao || 0).toFixed(1)}%</span>
                                <div className="progress-track">
                                    <div className="progress-fill" style={{ width: `${Math.min(metrics.risoSaturacao, 100)}%` }}></div>
                                </div>
                            </div>
                            <div className="infra-details">
                                <div><small>USUÁRIOS SIMULTÂNEOS</small> <strong>{metrics.novaCarga}</strong></div>
                                <div><small>CAPACIDADE TOTAL</small> <strong>{baseData.serverCapacity}</strong></div>
                            </div>
                        </section>

                        <section className="nexus-card insight-card">
                            <h3><FaLightbulb /> AI Insight</h3>
                            <div className="insight-content">
                                {metrics.risoSaturacao > 80 ? (
                                    <p className="danger-txt"><FaExclamationTriangle /> <strong>ALERTA:</strong> A carga simulada exige migração para instâncias de maior performance ou Cluster Kubernetes.</p>
                                ) : (
                                    <p className="success-txt">O ecossistema suporta a carga projetada com margem de segurança de {(100 - metrics.risoSaturacao).toFixed(1)}%.</p>
                                )}
                            </div>
                        </section>
                    </div>

                    <section className="nexus-card chart-card">
                        <h3>Projeção de Escala (12 Meses)</h3>
                        <div className="nexus-chart-container">
                            <Line 
                                data={{
                                    labels: ['Mês 1', 'Mês 3', 'Mês 6', 'Mês 9', 'Mês 12'],
                                    datasets: [{
                                        label: 'Carga Projetada (req/s)',
                                        data: [metrics.novaCarga, metrics.novaCarga * 1.2, metrics.novaCarga * 1.5, metrics.novaCarga * 1.8, metrics.novaCarga * 2.2],
                                        borderColor: '#00f2ff',
                                        backgroundColor: 'rgba(0, 242, 255, 0.1)',
                                        fill: true,
                                        tension: 0.4
                                    }]
                                }} 
                                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }}
                            />
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}